<template>
  <div class="clearfix" @mouseover="hover = true" @mouseleave="hover = false">
    <div v-if="locked" :class="{ error: validationErrors.length }">
      <div class="category-selector">
        <div
          v-for="(selector, index) in categories"
          :key="selector.key"
          class="form-group"
          :class="{ 'form-group-subordinate': selector.isChild }"
        >
          <locked-category-icon v-if="selector.isRoot" />
          <label v-if="selector.isRoot" :for="'category-selector-' + index">
            {{ lang.get('entries.form.category.label') }}
          </label>
          <settings
            :add-icon-visible="index === 0 && addIconVisible"
            :edit-icon-visible="editIconVisible(selector)"
            @add="insertCategory"
            @edit="onConfigure(selector.current)"
          />
          <select-field
            :id="'category-selector-' + index"
            :items="items(selector.items)"
            :value="selector.current"
            disabled="true"
          />
        </div>
      </div>
      <validation-errors :validation-errors="validationErrors" />
    </div>
    <div
      v-else
      :class="{ error: validationErrors.length, 'category-selector': true }"
    >
      <div
        v-for="(selector, index) in categories"
        :key="selector.key"
        class="form-group"
        :class="{ 'form-group-subordinate': selector.isChild }"
      >
        <label v-if="selector.isRoot" :for="'category-selector-' + index">
          {{ lang.get('entries.form.category.label') }}
        </label>
        <settings
          :add-icon-visible="index === 0 && addIconVisible"
          :edit-icon-visible="editIconVisible(selector)"
          @add="insertCategory"
          @edit="onConfigure(selector.current)"
        />
        <select-field
          :id="'category-selector-' + index"
          :items="items(selector.items)"
          :value="selector.current"
          aria-label="subcategory selector"
          @selected="onSelect"
        />
      </div>
      <validation-errors :validation-errors="validationErrors" />
    </div>
    <confirmation-modal
      v-if="selectedCategoryId"
      :key="`modal-target-configure-field-${selectedCategoryId}`"
      :modal-id="`modal-target-configure-field-${selectedCategoryId}`"
      :show-modal="confirmConfiguration"
      :confirmation="lang.get('miscellaneous.unsaved_changes')"
      :confirm-button-label="lang.get('buttons.continue')"
      :cancel-button-label="lang.get('buttons.cancel')"
      @closed="confirmConfiguration = false"
      @confirmed="configure"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { SelectField } from 'vue-bootstrap';
import ConfirmationModal from '@/lib/components/Shared/ConfirmationModal';
import ValidationErrors from '@/lib/components/Fields/ValidationErrors.vue';
import LockedCategoryIcon from './LockedCategoryIcon';
import Settings from './Settings';

export default {
  inject: ['lang'],
  components: {
    ConfirmationModal,
    SelectField,
    LockedCategoryIcon,
    ValidationErrors,
    Settings
  },
  data() {
    return {
      hover: false,
      selectedCategoryId: null,
      confirmConfiguration: false
    };
  },
  computed: {
    ...mapGetters('entryForm', [
      'categories',
      'categoryId',
      'categoriesIndex',
      'selectedChapter'
    ]),
    ...mapGetters('entryFormConfiguration', ['configurationActive']),
    ...mapState('entryForm', [
      'locks',
      'categoryTemplate',
      'hasManagerRole',
      'chapters',
      'chaptersLoaded',
      'canConfigureCategories'
    ]),
    ...mapState('entryFormApi', ['errorBag']),
    ...mapState('entryFormConfiguration', [
      'configurationInProgress',
      'configuredCategory',
      'configuredChapter'
    ]),
    locked() {
      return (
        this.locks.readOnly ||
        this.locks.lockedCategory ||
        this.configuredCategory ||
        this.configuredChapter ||
        !this.chaptersLoaded
      );
    },
    validationErrors() {
      return this.errorBag && this.errorBag.categoryId
        ? this.errorBag.categoryId.message
        : [];
    },
    addIconVisible() {
      return !!(
        this.hover &&
        this.chaptersLoaded &&
        this.canConfigureCategories &&
        !this.configurationActive
      );
    }
  },
  methods: {
    ...mapActions('entryForm', ['selectCategory', 'selectChapter']),
    ...mapActions('entryFormConfiguration', ['configureCategory']),
    ...mapMutations('entryForm', ['addCategory']),
    onSelect(name, selected) {
      this.selectCategory(selected);
    },
    onConfigure(categoryId) {
      this.selectedCategoryId = categoryId;

      if (this.configurationInProgress) {
        this.confirmConfiguration = true;
      } else {
        this.configure();
      }
    },
    configure() {
      const category = this.categoriesIndex.find(
        c => c.id === this.selectedCategoryId
      );
      if (category) {
        this.configureCategory({ slug: category.slug });
      }
    },
    newCategoryFromTemplate(categorySlug, chapterSlug) {
      this.addCategory({
        ...this.categoryTemplate,
        slug: categorySlug,
        chapterSlugs: [chapterSlug]
      });

      this.configureCategory({ slug: categorySlug });
    },
    insertCategory() {
      const slug = 'newcategory';

      if (this.selectedChapter) {
        this.newCategoryFromTemplate(slug, this.selectedChapter.slug);
      } else {
        const defaultChapter = this.chapters.find(c => c.locked === true);
        this.selectChapter(defaultChapter.id).then(() => {
          this.newCategoryFromTemplate(slug, defaultChapter.slug);
        });
      }
    },
    items(items) {
      // Return an array of items, append a suffix to inactive categories
      return items.map(item => ({
        id: item.id,
        name:
          this.hasManagerRole && !item.active && item.name !== '-'
            ? item.name + ' ' + this.lang.get('entries.form.inactive')
            : item.name
      }));
    },
    editIconVisible(selector) {
      return !!(
        this.hover &&
        this.chaptersLoaded &&
        this.canConfigureCategories &&
        selector.current &&
        selector.key !== 'placeholder' &&
        !this.configuredCategory
      );
    }
  }
};
</script>
